import styled, { css } from 'styled-components';
import { color, spacingDefaults, transition, shadow, radius } from '@kis-technologies/kis-ui';
import { PaginationButtonProps } from './types';
import { makeStyles, createStyles, MuiTheme } from '@kis-technologies/kis-ui';

export const systemsButtonPosition = (props: PaginationButtonProps) => {
  switch (props.position) {
    case 'left':
      return css`
        position: absolute;
        display: inline-flex;
        -webkit-box-align: center;
        align-items: center;
        height: 175px;
        width: 82px;
        background: #f46d2e;
        outline: none;
        padding: 0px 15px 0px 0px;
        user-select: none;
        transition: 0.2s ease-in-out opacity;
        left: 75px;
        top: calc(50% - 85px);
        border-top-right-radius: 150px;
        border-bottom-right-radius: 150px;
      `;
    case 'right':
      return css`
        position: absolute;
        display: inline-flex;
        -webkit-box-align: center;
        align-items: center;
        height: 175px;
        width: 82px;
        background: #f46d2e;
        outline: none;
        padding: 0px 0px 0px 15px;
        user-select: none;
        transition: 0.2s ease-in-out opacity;
        right: 75px;
        top: calc(50% - 85px);
        border-top-left-radius: 150px;
        border-bottom-left-radius: 150px;
      `;
    default:
      return null;
  }
};

export const SystemsPaginationButton = styled.button<PaginationButtonProps>`
  position: absolute;
  display: inline-flex;
  align-items: center;
  height: 265px;
  width: 265px;
  background: ${color.PRIMARY};
  padding: ${spacingDefaults[6]};
  outline: none;
  user-select: none;
  transition: 0.2s ease-in-out opacity;
  border: none;

  ${systemsButtonPosition};
`;

export const arrowButtonPosition = (props: PaginationButtonProps) => {
  switch (props.position) {
    case 'left':
      return css`
        position: absolute;
        display: inline-flex;
        -webkit-box-align: center;
        align-items: center;
        height: 175px;
        width: 82px;
        background: transparent;
        outline: none;
        padding: 0px 15px 0px 0px;
        user-select: none;
        transition: 0.2s ease-in-out opacity;
        left: 150px;
        top: calc(50% - 85px);
        border-top-right-radius: 150px;
        border-bottom-right-radius: 150px;
      `;
    case 'right':
      return css`
        position: absolute;
        display: inline-flex;
        -webkit-box-align: center;
        align-items: center;
        height: 175px;
        width: 82px;
        background: transparent;
        outline: none;
        padding: 0px 0px 0px 15px;
        user-select: none;
        transition: 0.2s ease-in-out opacity;
        right: 150px;
        top: calc(50% - 85px);
        border-top-left-radius: 150px;
        border-bottom-left-radius: 150px;
      `;
    default:
      return null;
  }
};

export const ArrowPaginationButton = styled.button<PaginationButtonProps>`
  position: absolute;
  display: inline-flex;
  align-items: center;
  height: 265px;
  width: 265px;
  background: ${color.PRIMARY};
  padding: ${spacingDefaults[6]};
  outline: none;
  user-select: none;
  transition: 0.2s ease-in-out opacity;
  border: none;

  ${arrowButtonPosition};
`;

interface BasicPaginationButtonProps {
  corner?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const cornerButtonStyle = (props: BasicPaginationButtonProps) =>
  props.corner &&
  css`
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-family: inherit;
    font-style: normal;
    font-weight: 600;
    border-style: solid;
    outline: none;
    max-width: 100%;
    width: auto;
    text-align: center;
    vertical-align: middle;
    text-decoration: none;
    transition: 0.2s ${transition.CURVE} all;
    padding: 0 24px;
    white-space: nowrap;
    height: 52px;
    border-width: 0px;
    font-size: 18.4px;
    color: ${color.WHITE};
    background: ${color.BLACK};
    opacity: 0.4;
    line-height: 0.5;
    user-select: none;
    transition: 0.2s ease-in-out opacity;
    ${'min-width: 160px; padding: 0 12px'};
    box-shadow: ${shadow.action.BASE};
    &:hover {
      box-shadow: ${shadow.action.HOVER};
    }
    border-radius: ${radius.ROUND};
    &[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
    }
    svg {
      height: 24px;
      width: 19px;
    }
  `;

export const PaginationButton = styled.button<BasicPaginationButtonProps>`
  ${cornerButtonStyle}
  ${(props) =>
    !props.corner &&
    css`
      padding: 0 24px;
      border-width: 0px;
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: 26.4px;
      font-weight: 600;
      height: 52px;
      width: 52px;
      line-height: 0.5;
      border-radius: 26px;
      color: ${color.WHITE};
      background: ${color.BLACK};
      opacity: 0.4;
      outline: none;
      user-select: none;
      transition: 0.2s ease-in-out opacity;

      &:first-child {
        margin-left: 0;
      }

      &[disabled] {
        opacity: 1;
      }
    `};
  ${(props) =>
    !props.disabled &&
    css`
      cursor: pointer;
    `};

  ${(props) =>
    props.corner &&
    css`
      opacity: 1;

      svg path {
        fill: ${color.WHITE};
        transition: 0.2s ease-in-out fill;
      }


    }
  `};
`;

export const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  width: 100%;

  ${PaginationButton}:first-child {
    padding-right: 10px;
  }

  ${PaginationButton}:last-child {
    padding-left: 10px;
  }
`;

export const PaginationButtonContent = styled.span`
  flex: 1 1 0%;
`;

export const PaginationIconContainer = styled.span`
  flex: 0 1 0%;
`;

export const ButtonContent = styled.span`
  flex: 1 1 0%;
`;

export const EllipsisContent = styled.div`
  flex: 1 1 0%;
  color: ${color.BLACK};
  opacity: 0.4;
  margin-bottom: 10px;
`;
export const useStyles = makeStyles((theme: MuiTheme) =>
  createStyles({
    nav: {
      minWidth: '100%',
      display: 'flex',
      justifyContent: 'space-around'
    },
    ul: {
      minWidth: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      padding: 0,
      margin: 0,
      listStyle: 'none'
    },
    li: {
      margin: '0 10px',
      '&:first-child': {
        display: 'flex',
        flex: '1 0 150px'
      },
      '&:last-child': {
        display: 'flex',
        flex: '1 0 150px',
        justifyContent: 'flex-end'
      }
    },
    systemPaginationPrev: {
      [theme.breakpoints.only('lg')]: {
        left: '35px'
      }
    },
    systemPaginationNext: {
      [theme.breakpoints.only('lg')]: {
        right: '35px'
      }
    },
    arrowPaginationPrev: {
      [theme.breakpoints.down('lg')]: {
        left: '120px'
      },
      [theme.breakpoints.only('lg')]: {
        left: '75px'
      }
    },
    arrowPaginationNext: {
      [theme.breakpoints.down('lg')]: {
        right: '120px'
      },
      [theme.breakpoints.only('lg')]: {
        right: '75px'
      }
    }
  })
);
